/**
 * Unity WebGL 到 Web 的消息通信桥接
 *
 * Unity 端会调用 window.dispatchWebUnityEvent(jsonString) 来发送消息
 * 这个模块负责注册全局函数并将消息转发给 React 组件
 */

// ============================================================
// 全局类型扩展
// ============================================================

declare global {
  interface Window {
    dispatchWebUnityEvent?: (type: 'unity_message', message: string) => void;
    dispatchReactUnityEvent?: (type: string, args: unknown) => void;
  }
}

// ============================================================
// 常量定义
// ============================================================

/** Unity 接收对象名 */
export const UNITY_RECEIVER = 'MainControl' as const;

/** Unity 消息方法名枚举 */
export const UnityMessageMethod = {
  /** 跳转页面/场景 */
  // ToPage: 'ToPage',
  /** 播放针刺动画 */
  // PlayNeedleFilm: 'PlayNeedleFilm',
  /** 播放定位动画 */
  // PlayPositionFilm: 'PlayPositionFilm',
  /** 退出动画 */
  // ExitFilm: 'ExitFilm',
  /** 打开调色板 */
  OpenPalette: 'OpenPalette',
  /** 退出调色板 */
  ExitPalette: 'ExitPalette',
  /** 切换模型 */
  ChangeModel: 'ChangeModel',
  /** 设置用户信息 */
  SetUserInfo: 'SetUserInfo',
  /** 光标指示器 */
  IndicatorActive: 'IndicatorActive',
  /** 设置Unity日志调试开关 */
  SetDebug: 'SetDebug',
  /** 模型操作灵敏度 */
  SetModelResponseRate: 'SetModelResponseRate',
  /** 切换穴位数据源（董氏奇穴 或 默认） */
  ChangePointSource: 'ChangePointSource',
  /** 手动加载解剖模型-一般是加载男性解剖模型 */
  LoadSubModel: 'LoadSubModel',
  /** 重置相机视角 */
  CameraFocus: 'CameraFocus',
  /** 取消选择聚焦 */
  CancelSelect: 'CancelSelect',
  /** 按模型ID高亮经络显示 */
  MeridianHighlightByModelId: 'MeridianHighlightByModelId',
  /** 按模型ID显示经络循行 */
  ShowMeridianCircleByModelId: 'ShowMeridianCircleByModelId',
  /** 按模型ID隐藏经络循行 */
  HideMeridianCircleByModelId: 'HideMeridianCircleByModelId',
  /** 显示/隐藏内络透视 */
  RenderInsideMeridian: 'RenderInsideMeridian',
  /** 模型截图 */
  Screenshot: 'Screenshot',
  /** 显示模型 */
  ShowByModelId: 'ShowByModelId',
  /** 隐藏模型 */
  HideByModelId: 'HideByModelId',
  /** 模型透明度显示 */
  FadeByModelId: 'FadeByModelId',
  /** 模型不透明显示 */
  OpaqueByModelId: 'OpaqueByModelId',
  /** 显示穴位名称 */
  ShowAcupointName: 'ShowAcupointName',
  /** 隐藏穴位名称 */
  HideAcupointName: 'HideAcupointName',
  /** 深色模式 */
  DarkMode: 'DarkMode',
  /** 退出/返回操作 */
  OnEscape: 'OnEscape',
} as const;

/** 模型类型常量 */
export const ModelType = {
  MAN: 'MAN',
  WOMAN: 'WOMAN',
  BABY: 'BABY',
  HAND: 'HAND',
  FOOT: 'FOOT',
  EAR: 'EAR',
  MAN2D: 'MAN2D',
  HEAD: 'HEAD',
  FACE: 'FACE',
} as const;

/** 模型类型中文名称常量 */
export const ModelTypeStrChz = {
  MAN: '男性模型',
  WOMAN: '女性模型',
  BABY: '小儿模型',
  HAND: '手部反射区',
  FOOT: '足部反射区',
  EAR: '耳部反射区',
  MAN2D: '男性2D模型',
  HEAD: '头部反射区',
  FACE: '面部反射区',
} as const;

/** 场景类型常量 */
export const SceneType = {
  Model: 'Model',
  Simulator: '/simulator',
} as const;

/** 穴位数据源类型常量 */
export const PointSourceType = {
  /** 默认数据源 */
  DEFAULT: 'default',
  /** 董氏奇穴 */
  DSQX: 'dsqx',
} as const;

// ============================================================
// 类型定义
// ============================================================

/** Unity 消息方法类型 */
export type UnityMessageMethodType = (typeof UnityMessageMethod)[keyof typeof UnityMessageMethod];

/** 模型类型字符串 */
export type ModelTypeString = (typeof ModelType)[keyof typeof ModelType];

/** 场景类型字符串 */
export type SceneTypeString = (typeof SceneType)[keyof typeof SceneType];

/** 穴位数据源类型字符串 */
export type PointSourceTypeString = (typeof PointSourceType)[keyof typeof PointSourceType];

/** 布尔值字符串类型 */
export type BooleanString = 'true' | 'false';

// ============================================================
// 数据模型接口
// ============================================================

/** 用户信息数据模型 */
export interface UserInfo {
  [key: string]: unknown;
}

/** 应用信息数据模型 */
export interface AppInfo {
  [key: string]: unknown;
}

/** SetUserInfo 消息数据模型 */
export interface SetUserInfoData {
  userInfo: UserInfo;
  appInfo: AppInfo | string;
  host: string;
}

/** CameraFocus 消息数据模型 */
export interface CameraFocusData {
  x?: number;
  y?: number;
  z?: number;
}

// ============================================================
// 消息数据类型映射
// ============================================================

/** 各方法对应的数据类型映射表 */
interface UnityMessageDataMap {
  [UnityMessageMethod.ChangeModel]: ModelTypeString | undefined;
  [UnityMessageMethod.SetUserInfo]: string; // JSON string of SetUserInfoData
  // [UnityMessageMethod.ToPage]: SceneTypeString;
  // [UnityMessageMethod.PlayNeedleFilm]: string;
  // [UnityMessageMethod.PlayPositionFilm]: string;
  // [UnityMessageMethod.ExitFilm]: string;
  [UnityMessageMethod.OpenPalette]: string;
  [UnityMessageMethod.ExitPalette]: string;
  [UnityMessageMethod.IndicatorActive]: BooleanString;
  [UnityMessageMethod.SetDebug]: BooleanString;
  [UnityMessageMethod.SetModelResponseRate]: '0' | '1' | '2'; // number as string
  [UnityMessageMethod.ChangePointSource]: PointSourceTypeString;
  [UnityMessageMethod.LoadSubModel]: string;
  [UnityMessageMethod.CameraFocus]: string; // JSON string of CameraFocusData or empty string
  [UnityMessageMethod.CancelSelect]: string;
  [UnityMessageMethod.MeridianHighlightByModelId]: string; // comma-separated model IDs
  [UnityMessageMethod.ShowMeridianCircleByModelId]: string; // comma-separated model IDs
  [UnityMessageMethod.HideMeridianCircleByModelId]: string; // comma-separated model IDs
  [UnityMessageMethod.RenderInsideMeridian]: BooleanString;
  [UnityMessageMethod.Screenshot]: string;
  [UnityMessageMethod.ShowByModelId]: string;
  [UnityMessageMethod.HideByModelId]: string;
  [UnityMessageMethod.FadeByModelId]: string;
  [UnityMessageMethod.OpaqueByModelId]: string;
  [UnityMessageMethod.ShowAcupointName]: string;
  [UnityMessageMethod.HideAcupointName]: string;
  [UnityMessageMethod.DarkMode]: BooleanString;
  [UnityMessageMethod.OnEscape]: string;
}

/** 根据方法类型获取对应的数据类型 */
export type UnityMessageData<T extends UnityMessageMethodType> = T extends keyof UnityMessageDataMap
  ? UnityMessageDataMap[T]
  : never;

// ============================================================
// 消息结构
// ============================================================

/** Unity 消息完整结构 */
export interface UnityMessage<T extends UnityMessageMethodType = UnityMessageMethodType> {
  receiver: typeof UNITY_RECEIVER;
  method: T;
  data: UnityMessageData<T>;
}

/** 向Unity发送消息的postMessage函数类型 */
export type UnitySendMessage = <T extends UnityMessageMethodType>(
  method: T,
  data: UnityMessageData<T>,
) => void;
